/**
 * What the world draws a planet from, whichever planet it is (the game's, the welcome planet, a
 * size in the story), and the measures every part of the world shares: radius and heights.
 */
import type { Grid } from '@bramvasterd/terrella-rules';

/** One height level, in world units. */
export const STEP = 0.26;
/** How high each kind stands above its level: water sits low; stone, ore and the deep layers at their level (9 and 10 are reserved). */
export const KIND_HEIGHT = [
  0, 0.025, 0.05, -0.07, 0.05, 0.05, 0.06, 0.06, 0, 0.02, 0.02, 0, 0.02, 0.02, 0.02, 0, 0, 0,
] as const;
export const radiusOf = (subdivisions: number): number => subdivisions * 1.05;

/**
 * The planet's inside, as the rules generate it (rules 19), and the blocks laid on it (rules 27):
 * the kind at a depth below the original surface, 0 the surface itself, down to the core; above
 * the surface (negative) the laid blocks. The walls of pits, craters and raised ground show it.
 */
export interface Layers {
  coreDepth: number;
  at(tile: number, depth: number): number;
}

export interface PlanetView {
  grid: Grid;
  kind: Uint8Array;
  /** Negative for a pit. */
  elev: Int8Array | Uint8Array;
  groups: Uint8Array;
  /** The layers under every tile; without them, walls keep their tile's colour. */
  layers?: Layers;
  /** Machines on their tiles (a resonator, a charge, …): what each is, how full, whether it works. */
  machines?: ReadonlyMap<number, MachineView>;
  /** Rock a charge broke (rules 24), lying on its tile to be gathered: 1. None when absent. */
  shattered?: Uint8Array;
  /** Ground an extractor draws from: its type's glow number (models/extractors.ts), 0 for none. It glows, connected. */
  drawn?: Uint8Array;
  /** Only these tiles are drawn (1), the rest left out: the Tile Lab's specimens. All when absent. */
  shown?: Uint8Array;
}

/** A machine on a tile, as the world draws it. */
export interface MachineView {
  /** Its type, as the rules name it: 'crystal' for the crystal resonator, 'charge'. */
  type: string;
  level: number;
  /** How full it is at server time `time`, 0 to 1. */
  fillAt(time: number): number;
  /** It is at work (drawing, digging): its turning part turns. */
  drawing: boolean;
}
